import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { I18nProvider } from "./i18n/I18nProvider";
import { ConfirmProvider } from "./contexts/ConfirmProvider";
import { SubscriptionProvider } from "./contexts/SubscriptionProvider";
import { OrgProvider } from "./contexts/OrgProvider";

// Pages
import LandingPage from "./pages/LandingPage";
import DemoRequest from "./pages/DemoRequest";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import ControlLibrary from "./pages/ControlLibrary";
import RiskRegister from "./pages/RiskRegister";
import Documents from "./pages/Documents";
import Incidents from "./pages/Incidents";
import SoA from "./pages/SoA";
import Evidence from "./pages/Evidence";
import Audits from "./pages/Audits";
import Mappings from "./pages/Mappings";
import ManagementReview from "./pages/ManagementReview";
import CorrectiveActions from "./pages/CorrectiveActions";
import Maintenance from "./pages/Maintenance";
import Reports from "./pages/Reports";
import ThreatIntelligence from "./pages/ThreatIntelligence";
import EmailSettings from "./pages/EmailSettings";
import GapAnalysis from "./pages/GapAnalysis";
import ImplementationWizard from "./pages/ImplementationWizard";
import CertificationReadiness from "./pages/CertificationReadiness";
import PolicyGenerator from "./pages/PolicyGenerator";
import VendorRisk from "./pages/VendorRisk";
import ComplianceCalendar from "./pages/ComplianceCalendar";
import RBACSettings from "./pages/RBACSettings";
import RiskHeatMap from "./pages/RiskHeatMap";
import VulnScanner from "./pages/VulnScanner";
import ExecutiveReport from "./pages/ExecutiveReport";
import TrainingModule from "./pages/TrainingModule";
import Benchmarking from "./pages/Benchmarking";
import BulkImportExport from "./pages/BulkImportExport";
import MultiTenancy from "./pages/MultiTenancy";
import ClientDashboard from "./pages/ClientDashboard";
import Billing from "./pages/Billing";
import FeatureGate from "./components/FeatureGate";
import ClientPortal from "./pages/ClientPortal";
import RiskPredictor from "./pages/RiskPredictor";
import EvidenceCollection from "./pages/EvidenceCollection";
import ESignatureSettings from "./pages/ESignatureSettings";
import PlatformAdmin from "./pages/PlatformAdmin";
import ZonaFrancaDashboard from "./pages/ZonaFrancaDashboard";
import ZonaFrancaOEA from "./pages/ZonaFrancaOEA";
import ZonaFrancaCNZFE from "./pages/ZonaFrancaCNZFE";
import ZonaFrancaISO22000 from "./pages/ZonaFrancaISO22000";
import ZonaFrancaBPM from "./pages/ZonaFrancaBPM";
import ZonaFrancaHACCP from "./pages/ZonaFrancaHACCP";
import ZonaFrancaNORDOM477 from "./pages/ZonaFrancaNORDOM477";
// S9: public Normas page — no auth required (ADR-S9-005)
import NormasPage from "./pages/NormasPage";
// S14a: password recovery pages — public, no ProtectedRoute
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
// S14a: audit log full page — super-admin only
import AuditLogPage from "./pages/AuditLogPage";
// S14b: 2FA setup wizard
import TwoFactorSetupPage from "./pages/TwoFactorSetupPage";
// S16a-2: audit detail full-page route (ADR-S16a-003)
import AuditDetail from "./pages/AuditDetail";
// S17b: Framework Picker — /settings/frameworks (ADR-S17b-003)
import FrameworkPicker from "./pages/FrameworkPicker";

// Components
import { Toaster } from "./components/ui/sonner";
import SidebarLayout from "./components/layout/SidebarLayout";
import ErrorBoundary from "./components/ErrorBoundary";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// Hardened axios defaults: timeout, attach token from localStorage,
// auto-redirect to /auth on 401 (token expired or invalid).
axios.defaults.timeout = 30000;
axios.interceptors.request.use((config) => {
  const t = localStorage.getItem("complianceos-token");
  if (t && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});
axios.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    // Don't bounce out of /auth/* flows
    if (status === 401 && !url.includes("/auth/")) {
      try { localStorage.removeItem("complianceos-token"); } catch {}
      if (!window.location.pathname.startsWith("/auth")) {
        window.location.href = "/auth";
      }
    }
    return Promise.reject(error);
  },
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("complianceos-theme");
    return saved || "dark";
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("complianceos-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === "dark" ? "light" : "dark");
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("complianceos-token"));
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem("complianceos-token");
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("complianceos-token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem("complianceos-token", authToken);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("complianceos-token");
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return children;
};

// App Layout with Sidebar for authenticated routes
const AppLayout = ({ children }) => {
  return (
    <SidebarLayout>
      {children}
    </SidebarLayout>
  );
};

// Main Router Component
const AppRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/demo" element={<DemoRequest />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/confirm" element={<AuthPage />} />
      {/* S14a: self-service password recovery — public, no ProtectedRoute */}
      <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset" element={<ResetPasswordPage />} />
      {/* S14a: org-invite email links use the same reset endpoint with an invite token */}
      <Route path="/auth/accept-invite" element={<ResetPasswordPage />} />
      {/* S9: public Normas page — all frameworks with description + value */}
      <Route path="/normas" element={<NormasPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/controls"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ControlLibrary />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/risks"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RiskRegister />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Documents />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/incidents"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Incidents />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/soa"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SoA />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evidence"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Evidence />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audits"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Audits />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* S16a-2: Full-page audit detail route (replaces Dialog modal — ADR-S16a-003) */}
      <Route
        path="/audits/:auditId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AuditDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mappings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Mappings />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/management-review"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ManagementReview />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/corrective-actions"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CorrectiveActions />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Maintenance />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Reports />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/threat-intel"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FeatureGate feature="threat_intelligence"><ThreatIntelligence /></FeatureGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FeatureGate feature="smtp_notifications"><EmailSettings /></FeatureGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gap-analysis"
        element={
          <ProtectedRoute>
            <AppLayout>
              <GapAnalysis />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wizard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FeatureGate feature="ai_implementation_wizard"><ImplementationWizard /></FeatureGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/certification"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CertificationReadiness />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/policy-generator"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FeatureGate feature="ai_policy_generator"><PolicyGenerator /></FeatureGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor-risk"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FeatureGate feature="vendor_risk"><VendorRisk /></FeatureGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ComplianceCalendar />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rbac"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FeatureGate feature="rbac_custom_roles"><RBACSettings /></FeatureGate>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/risk-heatmap"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RiskHeatMap />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/vuln-scanner" element={<ProtectedRoute><AppLayout><VulnScanner /></AppLayout></ProtectedRoute>} />
      <Route path="/exec-report" element={<ProtectedRoute><AppLayout><FeatureGate feature="executive_report"><ExecutiveReport /></FeatureGate></AppLayout></ProtectedRoute>} />
      <Route path="/training" element={<ProtectedRoute><AppLayout><TrainingModule /></AppLayout></ProtectedRoute>} />
      <Route path="/benchmarking" element={<ProtectedRoute><AppLayout><Benchmarking /></AppLayout></ProtectedRoute>} />
      <Route path="/bulk" element={<ProtectedRoute><AppLayout><FeatureGate feature="bulk_import_export"><BulkImportExport /></FeatureGate></AppLayout></ProtectedRoute>} />
      <Route path="/tenants" element={<ProtectedRoute><AppLayout><FeatureGate feature="multi_tenant"><MultiTenancy /></FeatureGate></AppLayout></ProtectedRoute>} />
      <Route path="/clients/:clientOrgId" element={<ProtectedRoute><AppLayout><ClientDashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><AppLayout><Billing /></AppLayout></ProtectedRoute>} />
      <Route path="/portal" element={<ProtectedRoute><AppLayout><ClientPortal /></AppLayout></ProtectedRoute>} />
      <Route path="/risk-predictor" element={<ProtectedRoute><AppLayout><FeatureGate feature="ai_risk_predictor"><RiskPredictor /></FeatureGate></AppLayout></ProtectedRoute>} />
      <Route path="/evidence-collection" element={<ProtectedRoute><AppLayout><FeatureGate feature="evidence_cloud_connectors"><EvidenceCollection /></FeatureGate></AppLayout></ProtectedRoute>} />
      <Route path="/esignature-settings" element={<ProtectedRoute><AppLayout><FeatureGate feature="esignature"><ESignatureSettings /></FeatureGate></AppLayout></ProtectedRoute>} />
      <Route path="/platform-admin" element={<ProtectedRoute><AppLayout><PlatformAdmin /></AppLayout></ProtectedRoute>} />
      {/* S14a: audit log full page — super-admin only */}
      <Route path="/platform-admin/audit-log" element={<ProtectedRoute><AppLayout><AuditLogPage /></AppLayout></ProtectedRoute>} />
      {/* S14b: 2FA setup/management wizard */}
      <Route path="/account/2fa" element={<ProtectedRoute><AppLayout><TwoFactorSetupPage /></AppLayout></ProtectedRoute>} />
      {/* Module 16 — Zona Franca Compliance */}
      <Route path="/zona-franca" element={<ProtectedRoute><AppLayout><FeatureGate feature="zona_franca"><ZonaFrancaDashboard /></FeatureGate></AppLayout></ProtectedRoute>} />
      <Route path="/zona-franca/oea" element={<ProtectedRoute><AppLayout><FeatureGate feature="zona_franca"><ZonaFrancaOEA /></FeatureGate></AppLayout></ProtectedRoute>} />
      <Route path="/zona-franca/cnzfe" element={<ProtectedRoute><AppLayout><FeatureGate feature="zona_franca"><ZonaFrancaCNZFE /></FeatureGate></AppLayout></ProtectedRoute>} />
      <Route path="/zona-franca/iso22000" element={<ProtectedRoute><AppLayout><FeatureGate feature="zona_franca"><ZonaFrancaISO22000 /></FeatureGate></AppLayout></ProtectedRoute>} />
      <Route path="/zona-franca/bpm" element={<ProtectedRoute><AppLayout><FeatureGate feature="zona_franca"><ZonaFrancaBPM /></FeatureGate></AppLayout></ProtectedRoute>} />
      <Route path="/zona-franca/haccp" element={<ProtectedRoute><AppLayout><FeatureGate feature="zona_franca"><ZonaFrancaHACCP /></FeatureGate></AppLayout></ProtectedRoute>} />
      <Route path="/zona-franca/nordom477" element={<ProtectedRoute><AppLayout><FeatureGate feature="zona_franca"><ZonaFrancaNORDOM477 /></FeatureGate></AppLayout></ProtectedRoute>} />
      {/* S17b: Framework Picker — ADR-S17b-003 */}
      <Route path="/settings/frameworks" element={<ProtectedRoute><AppLayout><FrameworkPicker /></AppLayout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <I18nProvider>
      <ConfirmProvider>
        <ThemeProvider>
          <AuthProvider>
            <SubscriptionProvider>
              <OrgProvider>
                <BrowserRouter>
                  {/* ErrorBoundary: catches render crashes (e.g. Pydantic v2 object in toast)
                      so the user sees a readable error screen instead of a white blank page. */}
                  <ErrorBoundary>
                    <AppRouter />
                    <Toaster position="top-right" richColors />
                  </ErrorBoundary>
                </BrowserRouter>
              </OrgProvider>
            </SubscriptionProvider>
          </AuthProvider>
        </ThemeProvider>
      </ConfirmProvider>
    </I18nProvider>
  );
}

export default App;
